// tenant/TenantOrderConfirmationPage.tsx
import React, { CSSProperties, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import { useTenantUserInfo } from '../../context/userInfoProvider';

type LocationState = {
  dateSelected: string;
  timeSelected: string;
  type: string;
};

const pageContainer: CSSProperties = {
  minHeight: '100vh',
  backgroundColor: '#261136',
  padding: 24,
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  fontFamily:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

const shellCard: CSSProperties = {
  width: '100%',
  maxWidth: 900,
  background:
    'radial-gradient(circle at top, #3A1F52 0, #170824 45%, #12061f 100%)',
  borderRadius: 16,
  padding: 24,
  boxSizing: 'border-box',
  boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
  color: '#F5F5FA',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  minHeight: 320,
};

const header: CSSProperties = {
  textAlign: 'center',
  marginBottom: 8,
  fontSize: 24,
  fontWeight: 600,
  color: '#AAB5E0',
};

const description: CSSProperties = {
  textAlign: 'center',
  fontSize: 14,
  opacity: 0.9,
  marginBottom: 16,
};

const row: CSSProperties = {
  marginBottom: 8,
};

const label: CSSProperties = {
  fontSize: 12,
  opacity: 0.75,
  textTransform: 'uppercase',
  letterSpacing: 0.7,
};

const value: CSSProperties = {
  fontSize: 15,
};

const footer: CSSProperties = {
  marginTop: 24,
  display: 'flex',
  justifyContent: 'center',
};

const primaryButton: CSSProperties = {
  padding: '10px 16px',
  borderRadius: 6,
  border: 'none',
  backgroundColor: '#7B1FA2',
  color: '#FFF',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

async function sendOrderEmail() {
  try {
    const apiKey = process.env.REACT_APP_SEND_EMAIL_API_KEY;

    await fetch(
      "https://yrigzkw0s9.execute-api.us-east-2.amazonaws.com/alpha/sendEmailFunction",
      {
        method: "POST",
        headers: apiKey
          ? { "X-API-Key": apiKey }
          : {},
      }
    );
  } catch (err) {
    console.error("Error sending order email", err);
  }
}

const TenantOrderConfirmationPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState | undefined>();
  const state = location.state;

  const {
    addressLine1,
    email, // if your userInfoProvider exposes this
  } = useTenantUserInfo() as { addressLine1?: string; email?: string };

  // fire email on mount if we have state
  useEffect(() => {
    if (!state) return;
    sendOrderEmail();
  }, [state]);

  if (!state) {
    return (
      <div style={pageContainer}>
        <div style={shellCard}>
          <h2>Order submitted</h2>
          <p>Your order has been requested.</p>
        </div>
      </div>
    );
  }

  const { dateSelected, timeSelected, type } = state;

  return (
    <div style={pageContainer}>
      <div style={shellCard}>
        <div>
          <div style={header}>Your order has been requested!</div>
          <div style={description}>
            We&apos;ll let you know once your order has been confirmed and a
            driver has been assigned.
          </div>

          <div style={row}>
            <div style={label}>Date</div>
            <div style={value}>
              {dateSelected
                ? moment(dateSelected).format('MMMM DD, YYYY')
                : 'N/A'}
            </div>
          </div>
          <div style={row}>
            <div style={label}>Time</div>
            <div style={value}>{timeSelected || 'N/A'}</div>
          </div>
          <div style={row}>
            <div style={label}>Address</div>
            <div style={value}>{addressLine1 || 'On file'}</div>
          </div>
          <div style={row}>
            <div style={label}>Order type</div>
            <div style={value}>{type}</div>
          </div>
        </div>

        <div style={footer}>
          <button
            type="button"
            style={primaryButton}
            onClick={() => history.replace('/tenant/home')}
          >
            Go to home
          </button>
        </div>
      </div>
    </div>
  );
};

export default TenantOrderConfirmationPage;
